import type { Participant } from '@fireflyhealth/zoom-web'
import { useRef, useState, useCallback } from 'react'
import { logger } from '~/shared/lib/logger'

interface ZoomEventInformation {
  description: string
  advice?: string
  documentationLink?: string
}

type EventInformationKey =
  | 'user-updated-muted'
  | 'user-updated-poor-network-member'
  | 'user-updated-poor-network-self'
  | 'user-updated-no-audio'
  | 'no-video-send-member'
type InformationMap = { [key in EventInformationKey]: ZoomEventInformation }

const ZoomEventNameToZoomEventInformation: InformationMap = {
  'user-updated-muted': {
    description: 'Member muted',
  },
  'user-updated-no-audio': {
    description: 'Member disconnected audio',
    advice: 'Device permissions or Bluetooth connection may be the issue',
  },
  'user-updated-poor-network-member': {
    description: 'The member has a poor network connection',
    advice: 'See if the member is able to connect using WiFi, or able to move to another location',
    documentationLink: 'https://developers.zoom.us/docs/video-sdk/web/quality/#network-quality',
  },
  'user-updated-poor-network-self': {
    description: 'You have a poor network connection',
    advice: 'Try connecting using WiFi or moving to another location',
    documentationLink: 'https://developers.zoom.us/docs/video-sdk/web/quality/#network-quality',
  },
  'no-video-send-member': {
    description: "The member's video is not connected",
    advice:
      'Is the camera allowed in device settings? Is another application also using the camera? Try having the member re-join the meeting or check their hardware (e.g. physical webcam connections)',
    documentationLink: 'https://developers.zoom.us/docs/video-sdk/web/quality/#video-quality',
  },
}

interface HelpEvent {
  information: ZoomEventInformation
  eventTime: number
}

/**
 * Manages provider-facing help for Zoom client events, e.g. the member being muted or having a poor connection
 */
export const useZoomEventHelp = ({ currentUserId }: { currentUserId?: number }) => {
  const [helpEvents, setHelpEvents] = useState<HelpEvent[]>([])
  const [newHelpEvent, setNewHelpEvent] = useState(false)
  const newEventTimer = useRef<NodeJS.Timeout | null>(null)
  // Successive video samples repeat the same failure, so it is reported once until the member's
  // video comes back.
  const isMemberVideoFailureReported = useRef(false)

  // Keeps the three most recent events, newest first.
  const addNewEventInfo = useCallback((informationKey: EventInformationKey) => {
    const information = ZoomEventNameToZoomEventInformation[informationKey]
    const newHelpEvent: HelpEvent = { information: information, eventTime: Date.now() }
    setHelpEvents(helpEvents => [newHelpEvent, ...helpEvents.slice(0, 2)])
    if (newEventTimer.current) {
      clearTimeout(newEventTimer.current)
    }
    setNewHelpEvent(true)
    newEventTimer.current = setTimeout(() => setNewHelpEvent(false), 5000)
    logger.info('Showed new Help Event', newHelpEvent)
  }, [])

  const handleEvent = function ({
    eventName,
    eventPayload,
  }: {
    eventName: 'user-updated' | 'network-quality-change' | 'video-statistic-data-change'
    // Opaque by design: each case below asserts the minimal shape it reads.
    eventPayload: unknown
  }) {
    // No current user ID yet? Call is just initializing and we can't tell which user events correspond to
    if (!currentUserId) return null
    // Each payload is cast to the minimal shape this handler reads, not the SDK's own event types.
    // CRM does not import the Zoom Web SDK directly; the shared package owns that dependency.
    switch (eventName) {
      case 'user-updated': {
        const [updated] = eventPayload as Participant[]
        if (!updated?.userId) return null
        if (currentUserId != updated.userId) {
          if (updated.muted) {
            addNewEventInfo('user-updated-muted')
          }
          if (updated.audio == '') {
            addNewEventInfo('user-updated-no-audio')
          }
        }
        break
      }
      case 'network-quality-change': {
        const typedPayload = eventPayload as { userId: number; level: number }
        if (typedPayload.level < 2) {
          if (currentUserId != typedPayload.userId) {
            addNewEventInfo('user-updated-poor-network-member')
          } else {
            addNewEventInfo('user-updated-poor-network-self')
          }
        }
        break
      }
      case 'video-statistic-data-change': {
        const typedPayload = eventPayload as { data: { encoding: boolean; fps: number } }
        if (typedPayload.data.encoding == false) {
          // These are metrics for received video
          // https://developers.zoom.us/docs/video-sdk/web/quality/#video-quality
          if (typedPayload.data.fps == 0 && !isMemberVideoFailureReported.current) {
            isMemberVideoFailureReported.current = true
            addNewEventInfo('no-video-send-member')
          } else if (typedPayload.data.fps > 0) {
            isMemberVideoFailureReported.current = false
          }
        }
        break
      }
    }
  }

  return { handleEvent, helpEvents, newHelpEvent }
}
